// packages/core/src/learning/school-modules/real-estate-housing.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Real estate & housing". surface: 'school',
// homeSchool: 'real-estate-housing'. module id 'school-real-estate-housing'
// matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #c9551f) — flag for the presentation-tuning pass.
// tier/pillar: housing is a major life decision spanning cash flow, leverage,
// and location → tier 'life-design', pillar 'life-decisions' (core module 11,
// Big Life Decisions) is the cleanest single fit. (Sibling income-life-domains
// schools career-capital/human-capital/income-expansion mapped to income; this
// one diverges because housing is a life-stage/location decision, not earning
// power. Flag for the presentation-tuning pass if you'd prefer alignment.)
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_REAL_ESTATE_HOUSING_MODULE: LearningModule = {
	id: 'school-real-estate-housing',
	title: 'Real estate & housing',
	description:
		'The lens that treats where you live as a money, leverage, location, and freedom tradeoff — testing rent, buy, and move decisions against real numbers instead of slogans.',
	emoji: '🏠', // PLACEHOLDER — tune in presentation pass
	accentColor: '#c9551f', // from SchoolMeta color — tune in presentation pass
	tier: 'life-design',
	pillar: 'life-decisions',
	order: 0,
	surface: 'school',
	homeSchool: 'real-estate-housing',
	lessons: [
		{
			id: 'school-real-estate-housing-01',
			title: 'Housing is a life tradeoff',
			description:
				'Learn why Real estate and housing sees shelter as a money decision, lifestyle decision, and freedom decision at the same time.',
			pillar: 'life-decisions',
			schoolsOfThought: ['real-estate-housing'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['housing-tradeoff', 'lifestyle-cost', 'shelter-choice'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['housing tradeoff', 'lifestyle cost', 'shelter choice'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Housing is usually the biggest line in your life. It decides where you sleep, who you see, how far you drive, how much cash you keep, and how trapped or free you feel.',
				},
				{
					type: 'paragraph',
					text: 'Real estate and housing believes shelter is never just shelter. Rent, a mortgage, roommates, location, commute, and ownership all shape your options. The question is not only can I afford this. The question is what does this housing choice do to my life?',
				},
				{
					type: 'bullet_list',
					items: [
						'Housing affects cash flow',
						'Housing affects time and energy',
						'Housing affects freedom and stability',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is judging housing only by the monthly payment.',
				},
				{
					type: 'example',
					text: 'Someone moves into a nicer place that costs $500 more per month. The space feels better, but their savings drops, their runway shrinks, and every job problem feels scarier. Another person pays more to live closer to work, saves eight commute hours per week, and uses that time to recover or earn more. Same higher cost, different tradeoff.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is trying to force one rule onto every situation. Some people need flexibility. Some need stability. Some need lower costs. Some need location. Some need space for family, health, work, or safety.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should push you to compare housing against your real numbers. Look at Dashboard, check your runway, and ask whether the home supports your freedom or quietly drains it.',
				},
				{
					type: 'key_takeaway',
					text: 'Housing is not just where you live. It is one of the biggest trades you make with your money.',
				},
			],
			audioScript:
				'Real estate and housing starts with a simple idea: where you live is not just a background detail. It is one of the largest money decisions in your life. It affects your monthly cash flow, your energy, your commute, your relationships, your safety, your routines, your career access, your stress, and your ability to change direction.\n\nMost people talk about housing in a shallow way. They ask, “Can I afford the payment?” That question matters, but it is not enough. A housing choice can technically fit your monthly income and still make your life feel tight. It can leave you with no runway. It can make every surprise repair feel like a crisis. It can force a long commute that drains your energy. It can keep you far from better work. It can make you dependent on a job you hate because the payment is too heavy.\n\nThis lens is not anti buying and it is not anti renting. It is against lazy thinking. Buying can create stability, control, roots, and possible wealth building. Renting can create flexibility, simplicity, and easier movement. Living with roommates can reduce pressure. Living alone can protect peace. Moving closer to work can save time. Moving farther away can save cash. There is no universal winner. There is only a tradeoff that either fits your real life or does not.\n\nTake a simple example. Someone upgrades to a place that costs $500 more per month. On paper, that is just a bigger payment. In real life, it could mean very different things. If the extra cost gives them safety, better sleep, a shorter commute, and a better mental state, it might support their life. If the extra cost wipes out savings and makes every paycheck feel tense, it might shrink their options. The same number can mean different things depending on the person, the location, and the rest of the money picture.\n\nThe mistake is judging housing by status. People say, “I should own by now,” or “renting is throwing money away,” or “I need the nicest place I can afford.” Those lines can push people into decisions they have not actually tested. A home can look like progress while quietly increasing pressure. A rental can look temporary while buying flexibility that matters during a career change or early life rebuild.\n\nInside Nisk, this lesson connects housing to your Dashboard and runway. Look at the payment. Look at the cash left after the payment. Look at how many months you could survive if income stopped. Look at what the location does to your time. Then ask the deeper question: does this home make my life stronger, or does it make me more fragile?\n\nHousing is emotional. That is normal. You are choosing a place to live, not just a spreadsheet row. But because it is emotional, the numbers matter even more. They keep the dream honest.',
			flashcards: [
				{
					id: 'school-real-estate-housing-01-fc-1',
					role: 'main_idea',
					front: 'What is housing really?',
					back: 'A money, lifestyle, location, and freedom tradeoff.',
				},
				{
					id: 'school-real-estate-housing-01-fc-2',
					role: 'watch_out',
					front: 'What is the shallow question?',
					back: 'Only asking whether the monthly payment fits.',
				},
				{
					id: 'school-real-estate-housing-01-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'How housing affects cash flow, runway, time, and options.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your housing load',
			},
			revision: 1,
		},
		{
			id: 'school-real-estate-housing-02',
			title: 'Buying is not always winning',
			description:
				'Learn why Real estate and housing challenges the idea that ownership is automatically better than renting.',
			pillar: 'life-decisions',
			schoolsOfThought: ['real-estate-housing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'ownership-cost',
				'renting-flexibility',
				'total-housing-cost',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'ownership cost',
				'renting flexibility',
				'total housing cost',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A lot of adults grow up hearing one line: renting is wasting money. Real estate and housing thinks that line is too simple.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes buying can be powerful, but only when the full cost fits. A mortgage payment is not the whole price. Ownership can include property taxes, insurance, maintenance, repairs, fees, interest, closing costs, stress, and less flexibility.',
				},
				{
					type: 'bullet_list',
					items: [
						'Renting buys flexibility',
						'Buying can buy control',
						'Both have hidden costs',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is comparing rent to only the mortgage payment.',
				},
				{
					type: 'example',
					text: 'Someone rents for $2,000 and sees a mortgage listed at $2,300. Buying sounds close. But after taxes, insurance, repairs, furniture, fees, and lost savings, the real cost may be much higher. Another buyer with strong reserves, stable plans, and a long time horizon may find ownership fits much better.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is emotional too. Ownership can feel grounding. Renting can feel temporary. But feelings should be paired with numbers, not used to ignore them.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a comparison exercise. Do not ask whether buying or renting is morally better. Ask what each choice does to your cash, flexibility, risk, and future options.',
				},
				{
					type: 'key_takeaway',
					text: 'Ownership can build control, but control has a full price.',
				},
			],
			audioScript:
				'Buying a home gets treated like the adult finish line. Rent gets treated like failure. That story is everywhere. It is in family conversations, social media, culture, and even casual money advice. Real estate and housing pushes back. Not because buying is bad. Buying can be great. But the idea that buying is automatically winning is too simple.\n\nThe biggest mistake is comparing rent to the mortgage payment as if those are the only two numbers. Renting has one clear monthly price in many cases. Ownership has layers. There is the mortgage, but also property taxes, insurance, repairs, maintenance, closing costs, possible homeowners association fees, furniture, appliances, landscaping, unexpected damage, and the cash you no longer have available for other goals. There is also the risk of being tied to one location when your life might change.\n\nRenting is not just paying someone else. Renting can buy flexibility. It can let you move for work, test a city, avoid repair risk, keep cash available, or stay lighter during a season when life is changing. That flexibility has value. It may not show up as equity, but it can show up as options.\n\nBuying can also be powerful. It can create stability. It can give you more control over your space. It can protect you from some rent increases. It can build equity over time if the numbers, time horizon, and location work. It can give a family roots. It can become part of a wealth plan. But none of that makes it automatically right. A house can build equity and still make your life stressful if the payment is too heavy or the reserves are too thin.\n\nImagine someone rents for $2,000 per month and sees a possible mortgage around $2,300. At first, buying looks close. But after taxes, insurance, repairs, and upfront costs, the real monthly burden could be much higher. If that person also has little emergency savings, unstable income, or a high chance of moving, ownership could create pressure. Now imagine someone else with strong savings, stable income, a long time horizon, and a location they truly want. For that person, buying may fit much better.\n\nThe point is not to crown renting or buying. The point is to stop letting slogans make the decision. “Rent is wasted money” ignores flexibility. “Buying is always a trap” ignores control and possible wealth building. Both statements are too lazy.\n\nInside Nisk, this lesson should make you test the tradeoff through your real numbers. What happens to your monthly surplus? What happens to your runway? What cash gets locked up? How long do you expect to stay? What repairs could you absorb? What opportunities would you lose if you could not move quickly?\n\nA good housing decision is not about proving you are a real adult. It is about choosing the structure that fits your life, your money, and your options.',
			flashcards: [
				{
					id: 'school-real-estate-housing-02-fc-1',
					role: 'main_idea',
					front: 'Is buying always better?',
					back: 'No. Ownership can help, but only when the full cost and life fit work.',
				},
				{
					id: 'school-real-estate-housing-02-fc-2',
					role: 'watch_out',
					front: 'What comparison is incomplete?',
					back: 'Comparing rent only to the mortgage payment.',
				},
				{
					id: 'school-real-estate-housing-02-fc-3',
					role: 'use_it',
					front: 'What should you count?',
					back: 'Taxes, insurance, repairs, fees, cash locked up, and flexibility.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Compare the full cost',
				prompt: 'Write the full cost of renting and the full cost of owning, including cash, risk, flexibility, and time.',
			},
			revision: 1,
		},
		{
			id: 'school-real-estate-housing-03',
			title: 'Leverage cuts both ways',
			description:
				'Learn why Real estate and housing treats debt as a tool that can magnify gains and magnify stress.',
			pillar: 'life-decisions',
			schoolsOfThought: ['real-estate-housing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['housing-leverage', 'debt-pressure', 'equity-risk'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['housing leverage', 'debt pressure', 'equity risk'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Real estate feels powerful because you can control a large asset with borrowed money. That is leverage. It can help, but it can also make mistakes heavier.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes housing debt deserves respect. Borrowing can help someone buy a home or property sooner than cash would allow. But the same debt can increase pressure when income drops, expenses rise, repairs hit, or the market moves against you.',
				},
				{
					type: 'bullet_list',
					items: [
						'Leverage can magnify upside',
						'Leverage can magnify stress',
						'Reserves decide how much pressure you can survive',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is seeing leverage as wealth without seeing the monthly obligation.',
				},
				{
					type: 'example',
					text: 'A buyer puts money down and property values rise over time. Leverage helped their equity grow. Another buyer stretches for the same loan, then faces a job loss and a major repair. The asset may still be valuable, but the payment does not care how stressed they are.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is treating real estate as safe just because it is physical. A house can be useful and still risky. A rental can produce income and still create vacancy, repairs, bad tenants, legal issues, and cash flow pressure.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects leverage to runway. Before a bigger housing decision, ask what happens if income drops or costs rise. The goal is not fear. The goal is respect for downside.',
				},
				{
					type: 'key_takeaway',
					text: 'Leverage can build wealth, but it also makes the payment louder.',
				},
			],
			audioScript:
				'One reason real estate feels so powerful is leverage. You can control a large property with borrowed money. That is different from needing the full purchase price in cash. If things go well, leverage can magnify your result. A smaller amount of your own money controls a larger asset, and if the property rises in value or produces income, the upside can feel impressive.\n\nBut leverage is not magic. It is debt. It creates an obligation. The payment arrives whether you feel confident or not. It arrives when work is stressful. It arrives when the roof leaks. It arrives when property taxes rise. It arrives when a tenant leaves. It arrives when your income changes. That is why Real estate and housing treats leverage with respect.\n\nThe optimistic version of leverage is easy to understand. Someone buys a home with a down payment. Over years, the property value rises, the loan balance drops, and equity grows. The borrowed money helped them participate in a larger asset than they could have bought with cash. That can be a real wealth path.\n\nNow look at the other side. Someone stretches to buy a property at the edge of what they can afford. At first, everything works. Then income drops, insurance rises, a repair hits, or a tenant stops paying. The property may still be valuable on paper, but the monthly payment becomes a pressure machine. Leverage did not just magnify the asset. It magnified the stress.\n\nThis is where reserves matter. Cash reserves are not boring in real estate. They are oxygen. They buy time when something goes wrong. They keep a repair from becoming a crisis. They give you space when a job changes. They help you avoid panic decisions. Without reserves, a property can own you more than you own it.\n\nThis lens also challenges the idea that real estate is automatically safe because it is physical. A building is real. Land is real. Shelter is real. But real does not mean risk free. Markets change. Neighborhoods change. Interest costs matter. Maintenance is real. Liquidity is limited. Selling can take time and money. Rental property can produce income, but it can also bring vacancy, repairs, management stress, legal rules, and concentration risk.\n\nNone of this means leverage is bad. It means leverage should be tested. Inside Nisk, ask what a bigger housing payment does to your runway. Ask how many months you could survive if income changed. Ask what repair size would stress your plan. Ask whether the property still fits if life gets less perfect.\n\nThe goal is not fear. The goal is respect. Real estate can be a strong path, but the debt side needs clear eyes. Leverage can help you move faster. It can also make a wrong move harder to escape.',
			flashcards: [
				{
					id: 'school-real-estate-housing-03-fc-1',
					role: 'main_idea',
					front: 'What is leverage?',
					back: 'Using borrowed money to control a larger asset.',
				},
				{
					id: 'school-real-estate-housing-03-fc-2',
					role: 'watch_out',
					front: 'What does leverage magnify?',
					back: 'It can magnify upside, but also stress, losses, and obligations.',
				},
				{
					id: 'school-real-estate-housing-03-fc-3',
					role: 'use_it',
					front: 'What protects you?',
					back: 'Runway, reserves, and a plan for income drops or surprise costs.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your housing runway',
			},
			revision: 1,
		},
		{
			id: 'school-real-estate-housing-04',
			title: 'Location changes the math',
			description:
				'Learn why Real estate and housing sees location as income, expenses, time, and lifestyle combined.',
			pillar: 'life-decisions',
			schoolsOfThought: ['real-estate-housing'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['location-economics', 'commute-cost', 'life-access'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['location economics', 'commute cost', 'life access'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Location is not just a vibe. It changes your money. It changes your time. It changes who you meet, where you work, how you travel, and what opportunities are easy or hard.',
				},
				{
					type: 'paragraph',
					text: 'Real estate and housing believes location belongs inside the money conversation. A cheaper place can cost more if it adds transportation, lost time, isolation, or weaker job access. A more expensive place can make sense if it improves income access, safety, energy, or support.',
				},
				{
					type: 'bullet_list',
					items: [
						'Commute has a cost',
						'Job access has value',
						'Community affects stability',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is comparing rent or mortgage prices without comparing the life around them.',
				},
				{
					type: 'example',
					text: 'One person saves $400 per month by moving far away, but adds ten commute hours per week and higher car costs. Another pays more to live near work and support, but spends less on transportation and has more energy. The cheaper address may not be cheaper in real life.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that location can be emotional. Family, culture, dating, safety, weather, schools, work, and identity all matter. Money is part of the choice, not the whole choice.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a full life check. Before moving, buying, or staying, ask what the location does to income, expenses, time, relationships, and runway.',
				},
				{
					type: 'key_takeaway',
					text: 'The price of a place is not only the payment. It is the life that payment creates.',
				},
			],
			audioScript:
				'Location is one of the most underestimated money variables. People look at rent or mortgage prices and think they are comparing places. But they are really comparing whole lives. A location changes your commute, your transportation costs, your job access, your dating life, your family support, your safety, your energy, your routines, your food options, your network, and your ability to recover.\n\nReal estate and housing believes location has to be counted, not just felt. A cheaper place can be a smart move. Lower housing cost can build runway, reduce pressure, and give you breathing room. But cheaper is not always cheaper in real life. If the lower price adds a long commute, higher gas costs, more car wear, fewer job options, more isolation, or less safety, the savings may be weaker than they look.\n\nA more expensive place can also be risky. Paying more for a popular area can drain your cash flow and make life fragile. But sometimes a higher cost buys access. Access to better work. Access to transit. Access to family help. Access to community. Access to a gym, school, clinic, or social life that keeps you stable. The point is not that expensive is better. The point is that location creates both costs and benefits.\n\nImagine someone saves $400 per month by moving far away from work. On paper, that looks like discipline. But the commute adds ten hours per week, more gas, more stress, and less sleep. Their food spending rises because they are tired. Their job performance slips because they are drained. Now the move is not just a cheaper rent decision. It is a time and energy tradeoff.\n\nNow imagine someone pays $300 more to live near work, family, or a better job market. That cost could be irresponsible if it wipes out savings. But it could be reasonable if it cuts transportation, improves energy, increases career access, and makes the rest of the plan easier to follow. Again, the number alone does not tell the full truth.\n\nLocation is also emotional. People choose places because of family, culture, identity, dating, weather, safety, schools, caregiving, and belonging. Those are real. Nisk should not reduce the decision to a spreadsheet. But the spreadsheet helps you see the consequences. You can honor the emotional side and still test the money side.\n\nInside Nisk, use this lens before moving, buying, or staying. Look at Dashboard. What does the current location cost you? Use reflection. What does the location give you? Use Simulate. What changes if housing goes up or down? Ask what happens to your time, energy, runway, and income access.\n\nA place is never just a place. It is a structure around your life. Choose with open eyes.',
			flashcards: [
				{
					id: 'school-real-estate-housing-04-fc-1',
					role: 'main_idea',
					front: 'Why does location matter?',
					back: 'It affects income, expenses, time, energy, relationships, and options.',
				},
				{
					id: 'school-real-estate-housing-04-fc-2',
					role: 'watch_out',
					front: 'What is the location mistake?',
					back: 'Comparing payments without comparing commute, access, and life cost.',
				},
				{
					id: 'school-real-estate-housing-04-fc-3',
					role: 'use_it',
					front: 'What should you check?',
					back: 'Housing cost, transportation, job access, support, safety, and runway.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Audit the location tradeoff',
				prompt: 'Write what your location costs you and what it gives you in money, time, energy, and support.',
			},
			revision: 1,
		},
		{
			id: 'school-real-estate-housing-05',
			title: 'Property is not passive',
			description:
				'Learn why Real estate and housing treats rental income and ownership as work, risk, and responsibility.',
			pillar: 'life-decisions',
			schoolsOfThought: ['real-estate-housing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['rental-reality', 'ownership-work', 'cash-flow-risk'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['rental reality', 'ownership work', 'cash flow risk'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Real estate income gets marketed like it arrives while you sleep. Sometimes property can produce cash flow, but the word passive hides too much.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes property ownership is a business like responsibility. Rental income has to survive vacancy, repairs, financing, taxes, insurance, tenants, rules, time, and management. The rent number is not the profit number.',
				},
				{
					type: 'bullet_list',
					items: [
						'Vacancy can erase income',
						'Repairs can crush cash flow',
						'Management takes time or money',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is counting rent before counting reality.',
				},
				{
					type: 'example',
					text: 'A rental brings in $2,400 per month. That sounds strong until the owner counts mortgage, taxes, insurance, repairs, vacancy reserves, management, and a major appliance replacement. The property may still work, but only after the real costs are included.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is falling in love with the asset and ignoring concentration risk. One property can mean one location, one tenant pool, one loan, and one set of expensive surprises.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a stress test. If a housing or property idea only works when nothing breaks, no one moves out, and costs stay calm, the plan is too fragile.',
				},
				{
					type: 'key_takeaway',
					text: 'Property can create income, but it is not magic money. It is ownership with obligations.',
				},
			],
			audioScript:
				'Real estate has a powerful story around it: buy property, collect rent, build wealth. There is truth in that story. Property can create income. It can build equity. It can become part of a serious wealth plan. But the clean version that gets sold online often leaves out the messy part. Property is not automatically passive.\n\nReal estate and housing wants the rent number to be treated carefully. If a unit rents for $2,400 per month, that is not the same as $2,400 of profit. You still have the loan payment if there is one. You have taxes. Insurance. Repairs. Maintenance. Vacancy. Possible management fees. Legal rules. Turnover. Appliances. Plumbing. Roofs. Tenants. Time. Stress. Capital expenses. The rent number is the top line, not the final result.\n\nThis does not mean rental property is bad. It means rental property is a business like responsibility. Even if someone hires a manager, the owner is still responsible for the asset, the debt, the reserves, and the decisions. If the property is vacant for two months, the payment still exists. If a major repair hits, the bill still exists. If a tenant damages the place or the local market changes, the spreadsheet has to absorb reality.\n\nHere is a simple example. A property brings in $2,400 per month in rent. Online, someone might call that cash flow. But after the mortgage, taxes, insurance, repairs, vacancy reserve, management, and a new water heater, the actual surplus may be much smaller. It may even be negative in some months. The property might still be a good long term investment for someone with reserves and patience. But it is not free money.\n\nThe other risk is concentration. One property can tie a lot of money to one address, one neighborhood, one loan, one tenant pool, and one local market. That can work, but it is not the same as being broadly diversified. If something goes wrong, it can be expensive and very personal. Stocks do not usually call you about a broken pipe at midnight. Property can.\n\nThis is why real estate needs reserves. Reserves are not optional decoration. They are part of the business model. Without them, every repair becomes panic. Every vacancy becomes pressure. Every surprise becomes a threat to your personal finances. The stronger the reserves, the more the owner can make decisions calmly.\n\nInside Nisk, this lesson should make you stress test property ideas. What happens if rent stops for one month? What happens if a repair costs $3,000? What happens if insurance rises? What happens if your own income changes at the same time? If the idea only works when nothing breaks, the idea is fragile.\n\nProperty can be powerful, but it deserves respect. The real lesson is not “never buy property.” The lesson is “do not call it passive until you have counted the work.”',
			flashcards: [
				{
					id: 'school-real-estate-housing-05-fc-1',
					role: 'main_idea',
					front: 'Is property passive?',
					back: 'Not fully. Ownership includes work, risk, reserves, and responsibility.',
				},
				{
					id: 'school-real-estate-housing-05-fc-2',
					role: 'watch_out',
					front: 'What number misleads people?',
					back: 'Rent before expenses, vacancy, repairs, financing, and management.',
				},
				{
					id: 'school-real-estate-housing-05-fc-3',
					role: 'use_it',
					front: 'How do you test property?',
					back: 'Stress test vacancy, repairs, reserves, and concentration risk.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Stress test property costs',
				scenarioType: 'custom',
			},
			revision: 1,
		},
	],
};
